// Shared client/server upload validation rules for card images.
// Keep in sync between the browser (src/routes/sell.tsx) and the
// server verification function (src/lib/card-image-verify.functions.ts).

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB

export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export type AllowedImageMime = (typeof ALLOWED_IMAGE_MIME_TYPES)[number];

// Map mime -> canonical extension (used to build the storage path so a
// crafted filename like "payload.php" cannot bleed into the object key).
export const MIME_TO_EXT: Record<AllowedImageMime, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

// Filename extensions accepted from the browser file picker. `jpeg` maps
// to the same mime as `jpg`.
export const ALLOWED_IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"] as const;

// Extensions we explicitly refuse even if the browser reports an
// image-ish mime type.
export const BLOCKED_EXTENSIONS = [
  "svg",
  "pdf",
  "exe",
  "js",
  "mjs",
  "cjs",
  "html",
  "htm",
  "php",
  "sh",
  "bat",
];

export type ImageValidationResult =
  | { ok: true; mime: AllowedImageMime; ext: string }
  | { ok: false; reason: string };

export function validateImageFile(file: {
  name: string;
  type: string;
  size: number;
}): ImageValidationResult {
  if (file.size <= 0) return { ok: false, reason: `${file.name}: empty file` };
  if (file.size > MAX_IMAGE_BYTES) {
    return {
      ok: false,
      reason: `${file.name}: exceeds 10MB limit`,
    };
  }

  const lowerName = file.name.toLowerCase();
  const nameExt = lowerName.includes(".")
    ? lowerName.slice(lowerName.lastIndexOf(".") + 1)
    : "";

  if (BLOCKED_EXTENSIONS.includes(nameExt)) {
    return { ok: false, reason: `${file.name}: file type not allowed` };
  }

  const mime = file.type.toLowerCase();
  if (!ALLOWED_IMAGE_MIME_TYPES.includes(mime as AllowedImageMime)) {
    return {
      ok: false,
      reason: `${file.name}: only JPG, PNG and WEBP images are allowed`,
    };
  }

  if (nameExt && !ALLOWED_IMAGE_EXTENSIONS.includes(nameExt as (typeof ALLOWED_IMAGE_EXTENSIONS)[number])) {
    return {
      ok: false,
      reason: `${file.name}: only JPG, PNG and WEBP images are allowed`,
    };
  }

  return {
    ok: true,
    mime: mime as AllowedImageMime,
    ext: MIME_TO_EXT[mime as AllowedImageMime],
  };
}
